import { Component, ViewChild } from '@angular/core';
import { TableSelectionMode } from '../../../enum/table-selection-mode.enum';
import { ExampleMaster } from '../../models/example-master.model';
import { TabMenu } from 'primeng/tabmenu';
import { Option } from '../../../models/option.model';
import { ExampleData } from '../../models/example-data.model';
import { Observable, Subject, firstValueFrom, lastValueFrom, map, of, switchMap, takeUntil } from 'rxjs';
import { ExampleService } from '../../services/example.service';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DataAction } from '../../../enum/data-action.enum';
import { v4 as uuidv4 } from 'uuid';
import { ExampleMasterCondition } from '../../models/example-master-condition.model';
import { SimpleSearchInlineEditComponent } from '../../../components/abstract/simple/simple-search-inline-edit.component';

@Component({
  selector: 'app-example-search-inline-edit',
  templateUrl: './example-search-inline-edit.component.html',
  styleUrls: ['./example-search-inline-edit.component.scss']
})
export class ExampleSearchInlineEditComponent extends SimpleSearchInlineEditComponent {

  // single or multiple
  override tableSelectionMode: 'single' | 'multiple' = TableSelectionMode.MULTIPLE;

  // Component 的 class 名稱
  override componentClassName: string = 'ExampleSearchInlineEditComponent';

  // Table 資料的 key 欄位名稱
  override rowDataKeyName: string = 'uuid';

  // Table 資料刪除時要取哪個欄位的值來顯示提示
  override rowDataDeleteHintFieldName: string = 'input';

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override masterData: ExampleMaster | undefined;

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override detailDatas: ExampleMaster[] = [];

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override rowCurrentData: ExampleMaster| undefined;

  // 有 filter 功能
  override hasTableFilter: boolean = true;

  // 取得 Table 分頁頁籤物件，後面用來抓頁籤資訊用的
  @ViewChild('detailTabMenuComponent') override detailTabMenuComponent!: TabMenu;

  // 顯示單身批次刪除的按鈕
  override canBatchDeleteRows: boolean = true;

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override async initFormGroup(): Promise<any> {
    // 設定單頭 formGroup 內容
    this.initSearchFormGroup();
    // 設定單身 formGroup 內容
    this.initDetailFormGroup();
  }

  private async initSearchFormGroup(): Promise<void> {
    this.formGroup = this.formBuilder.nonNullable.group({
      input: '',
      calendar: '',
      dropdown: ['', Validators.required],
      autoCompleteCode: ''
    });
  }

  private async initDetailFormGroup(): Promise<void> {
    this.inlineFormGroup = this.formBuilder.nonNullable.group({
      id: '',
      uuid: '',
      input: ['', Validators.required],
      calendar: ['', Validators.required],
      dropdown: ['', Validators.required],
      autoCompleteCode: '',
      inputNumber: null,
      custValidation: ['', this.detailCustValidator()],
      dynamicDropdown: '',
      autoCompleteDropdownCode: ''
    });

    await this.setDynamicOptions();
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions end');
  }

  override initFormControlDisabled(): void {
    // 如果單頭表單欄位沒有特別需要控制不可編輯就不用寫
    return;
  }

  override initStaticOptions(): void {
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    this.formGroup.patchValue({
      // dropdown: 'Y'
    });
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  override initExtraDetailMenu(): void {
    this.detailMenu.push(
      {
        id: this.parseToId('btnDetailMenuSomeAction'),
        label: '選擇後的動作',
        automationId: '選擇後的動作',
        command: () => {
          this.someAction();
        }
      }
    );
  }

  override setExtraDetailMenuStatus(): void {
    let detailMenuButtonOther = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (detailMenuButtonOther) {
      detailMenuButtonOther.disabled = !this.rowSelectedDatas || this.rowSelectedDatas.length <= 0;
    }

    this.redrawDetailMenu();
  }

  override initTableColumns(): void {
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key

    // 先定義頁籤
    this.detailTabs = [
      {
        id: this.parseToId('detailTab1'),
        label: this.translateService.instant('Tab 1'),
        automationId: 'Tab 1'
      },
      {
        id: this.parseToId('detailTab2'),
        label: this.translateService.instant('Tab 2'),
        automationId: 'Tab 2'
      }
    ];
    this.detailTabMenuComponent.activeItem = this.detailTabs[0];

    // 設定頁籤 detailTab1 內的欄位
    this.detailTabColumns[this.parseToId('detailTab1')] = [
      {
        header: '文字輸入',
        field: 'input',
        required: true,
        sortable: true,
        filter: {
          type: 'text'
        }
      },
      {
        header: '選擇日期',
        field: 'calendar',
        required: true,
        sortable: false,
        filter: {
          type: 'date',
          matchMode: 'dateIs'
        }
      },
      {
        header: '下拉選單',
        field: 'dropdown',
        required: true,
        translateArrayValue: {
          datas: this.exampleOptions,
          valueField: 'value',
          displayField: 'label'
        },
        sortable: false,
        filter: {
          type: 'dropdown',
          options: this.exampleOptions,
          matchMode: 'equals'
        }
      },
      {
        header: '自動完成',
        field: 'autoCompleteName',
        inlineFormControlName: 'autoCompleteCode',
        translateDataValueField: 'autoCompleteName',
        sortable: false,
        filter: {
          type: 'text',
          field: 'autoCompleteFilter'
        }
      }
    ];

    // 設定頁籤 detailTab2 內的欄位
    this.detailTabColumns[this.parseToId('detailTab2')] = [
      {
        header: '數字輸入',
        field: 'inputNumber',
        numberField: true,
        sortable: true,
        filter: {
          type: 'numeric',
          matchMode: 'equals'
        }
      },
      {
        header: '動態下拉',
        field: 'dynamicDropdown',
        translateArrayValue: {
          datas: this.dynamicOptions,
          valueField: 'value',
          displayField: 'label'
        },
        sortable: true,
        filter: {
          type: 'dropdown',
          options: this.dynamicOptions,
          matchMode: 'equals',
          translateOptionFieldName: true
        }
      },
      {
        header: '自動完成+下拉',
        field: 'autoCompleteDropdownName',
        inlineFormControlName: 'autoCompleteDropdownCode',
        translateDataValueField: 'autoCompleteDropdownName',
        sortable: true,
        filter: {
          type: 'text'
        }
      },
      {
        header: '自訂欄位驗證',
        field: 'custValidation',
        filter: {
          type: 'text'
        },
        width: 350
      }
    ];

    // 把所有頁籤的欄位都設定到 Table Column 內
    this.tableColumns = [
      ...this.detailTabColumns[this.parseToId('detailTab1')],
      ...this.detailTabColumns[this.parseToId('detailTab2')]
    ];

    // 設定目前顯示第一個頁籤的欄位
    this.selectedColumns = this.detailTabColumns[this.parseToId('detailTab1')];
  }

  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    // 控制當有選取資料的時候按鈕才能按
    let menuItem = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (menuItem) {
      menuItem.disabled = selectedDataCount <= 0;
    }
    this.redrawDetailMenu();
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData =>
                exampleData.exampleDataName.indexOf(keyword!) >= 0
                || exampleData.exampleDataNameCn.indexOf(keyword!) >= 0
                || exampleData.exampleDataNameUs.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }

  detailCustValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('detailCustValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  someAction(): void {
    // 這裡是為了寫單選和複選的使用例子，實際上不會有程式一下是單選一下是複選?
    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.systemMessageService.info(this.rowSelectedDatas.input);
    } else {
      this.systemMessageService.info(this.rowSelectedDatas.map((data: ExampleMaster) => data.input).toString());
    }

    // 很重要，自己決定要不要把記錄下來選擇的資料清空
    this.clearRowSelectedDatas();
  }

  override generateNewRowData(): ExampleMaster {
    let newRowData: ExampleMaster = new ExampleMaster();
    newRowData.id = DataAction.NEW;
    newRowData.uuid = uuidv4();
    return newRowData;
  }

  override async onPatchEditableRow(event: {data: ExampleMaster}): Promise<any> {
    console.log('patchEditableRow event = ' + JSON.stringify(event.data));

    this.inlineFormGroup.patchValue({
      id: event.data.id,
      uuid: event.data.uuid,
      input: event.data.input,
      calendar: event.data.calendar,
      dropdown: event.data.dropdown,
      autoCompleteCode: event.data.autoCompleteCode ? {
        "exampleDataCode": event.data.autoCompleteCode,
        "exampleDataName": event.data.autoCompleteName,
        "exampleDataNameTw": event.data.autoCompleteNameTw,
        "exampleDataNameCn": event.data.autoCompleteNameCn,
        "exampleDataNameUs": event.data.autoCompleteNameUs
      } : null,
      inputNumber: event.data.inputNumber,
      custValidation: event.data.custValidation,
      dynamicDropdown: event.data.dynamicDropdown,
      autoCompleteDropdownCode: event.data.autoCompleteDropdownCode ? {
        "exampleDataCode": event.data.autoCompleteDropdownCode,
        "exampleDataName": event.data.autoCompleteDropdownName,
        "exampleDataNameTw": event.data.autoCompleteDropdownNameTw,
        "exampleDataNameCn": event.data.autoCompleteDropdownNameCn,
        "exampleDataNameUs": event.data.autoCompleteDropdownNameUs
      } : null
    });
  }

  override async doSaveEditableRow(): Promise<boolean> {
    console.log('doSaveEditableRow tableFormGroup = ' + JSON.stringify(this.inlineFormGroup.getRawValue()));

    if (!this.inlineFormGroup.valid) {
      return false;
    }

    let savedData: ExampleMaster = new ExampleMaster();
    // 存檔後如果 id 是 new 表示新增的資料，把 id 清空，之後才不會被判斷成是新增未存檔的資料
    savedData.id = this.inlineFormGroup.getRawValue().id != DataAction.NEW ? this.inlineFormGroup.getRawValue().id : '';
    savedData.uuid = this.inlineFormGroup.getRawValue().uuid;
    savedData.input = this.inlineFormGroup.getRawValue().input;
    savedData.calendar = this.inlineFormGroup.getRawValue().calendar;
    savedData.dropdown = this.inlineFormGroup.getRawValue().dropdown;
    savedData.autoCompleteCode = this.inlineFormGroup.getRawValue().autoCompleteCode?.exampleDataCode;
    savedData.autoCompleteName = this.inlineFormGroup.getRawValue().autoCompleteCode?.exampleDataName;
    savedData.autoCompleteNameTw = this.inlineFormGroup.getRawValue().autoCompleteCode?.exampleDataNameTw;
    savedData.autoCompleteNameCn = this.inlineFormGroup.getRawValue().autoCompleteCode?.exampleDataNameCn;
    savedData.autoCompleteNameUs = this.inlineFormGroup.getRawValue().autoCompleteCode?.exampleDataNameUs;
    savedData.setAutoCompleteFilter();
    savedData.inputNumber = this.inlineFormGroup.getRawValue().inputNumber;
    savedData.custValidation = this.inlineFormGroup.getRawValue().custValidation;
    savedData.dynamicDropdown = this.inlineFormGroup.getRawValue().dynamicDropdown;
    savedData.autoCompleteDropdownCode = this.inlineFormGroup.getRawValue().autoCompleteDropdownCode?.exampleDataCode;
    savedData.autoCompleteDropdownName = this.inlineFormGroup.getRawValue().autoCompleteDropdownCode?.exampleDataName;
    savedData.autoCompleteDropdownNameTw = this.inlineFormGroup.getRawValue().autoCompleteDropdownCode?.exampleDataNameTw;
    savedData.autoCompleteDropdownNameCn = this.inlineFormGroup.getRawValue().autoCompleteDropdownCode?.exampleDataNameCn;
    savedData.autoCompleteDropdownNameUs = this.inlineFormGroup.getRawValue().autoCompleteDropdownCode?.exampleDataNameUs;
    savedData.setAutoCompleteDropdownFilter();

    return this.updateDetailAndModifiedDatas(savedData);
  }

  override onCancelEditableRow(): void {
    return;
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  /**
   * 要自己實作實際查詢做的事情。
   * 欄位驗證成功後也要把查詢條件寫到 requeryCondition 內，供之後重新查詢使用。
   */
  override async doQuery(): Promise<boolean> {
    let exampleMasterCondition: ExampleMasterCondition = new ExampleMasterCondition();
    exampleMasterCondition.input = this.formControl('input').getRawValue();
    exampleMasterCondition.calendar = this.formControl('calendar').getRawValue();
    exampleMasterCondition.dropdown = this.formControl('dropdown').getRawValue();
    exampleMasterCondition.autoCompleteCode =
      !!this.formControl('autoCompleteCode').getRawValue() ? this.formControl('autoCompleteCode').getRawValue().exampleDataCode : '';
    console.log('doQuery exampleMasterCondition = ' + JSON.stringify(exampleMasterCondition));

    const data = await firstValueFrom(
      this.exampleService.queryExampleMasterDatas(exampleMasterCondition).pipe(
        takeUntil(this._destroying$)
      )
    );

    this.detailDatas = data ? data : [];
    // 如果 searchBeforeEditing = true，請各自程式一定要在 doQuery 成功後
    // 自己把 searched 更新成 true，重要!!
    this.searched = true;
    return true
  }

  /**
   * 要自己實作實際 Submit 的事件。
   */
  override async doDetailMenuSubmit(): Promise<boolean> {
    return new Promise(resolve => {
      // setTimeout 是故意模擬處理的時間，實際上不會這樣做
      setTimeout(() => {
        console.log('doDetailMenuSubmit modifiedDatas = ' + JSON.stringify(this.modifiedDatas));

        // TODO 實作存檔時要呼叫 API 存入 modifiedDatas 資料，成功後記得要呼叫傳入的 callback function
        // 因為這類型的操作不能重新查詢，為了維持前端輸入的結果
        // 後端實作時應以前端傳入的 uuid 寫入 DB，而不是後端產生

        this.systemMessageService.info('模擬實際存檔會花一點時間處理');
        resolve(true);
      }, 1000);
    });
  }
}
